import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { FiUsers, FiClock, FiCheck, FiChevronLeft, FiChevronRight, FiCalendar, FiActivity, FiX, FiMinus, FiAlertCircle, FiArrowRight, FiUserCheck, FiSettings, FiDownload, FiCamera, FiShield } from 'react-icons/fi';
import AttendanceSettingsModal from '../components/AttendanceSettingsModal';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import * as XLSX from 'xlsx-js-style';

const Attendance = () => {
  const { isAuthenticated, company } = useAuth();
  const [attendanceData, setAttendanceData] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]); // All field staff employees
  const [allSupervisors, setAllSupervisors] = useState([]); // All supervisors for the company
  const [departments, setDepartments] = useState([]); // Company departments for settings
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Track clicked date for highlighting
  const [selectedLogKey, setSelectedLogKey] = useState(null); // Highlight all days for a multi-day session/log
  const [expandedMaps, setExpandedMaps] = useState({}); // recordKey -> boolean
  const [expandedBreaks, setExpandedBreaks] = useState({});
  const [focusedMapPoint, setFocusedMapPoint] = useState(null); // { lat, lng, kind, time, address }
  const [mapResetTick, setMapResetTick] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showEnrolledFace, setShowEnrolledFace] = useState(false); // For enrolled face modal
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force re-fetch attendance data
  const exportMenuRef = React.useRef(null);
  
  // Function to refresh attendance data
  const refreshAttendance = () => setRefreshTrigger(prev => prev + 1);
  const [settingsDepartmentId, setSettingsDepartmentId] = useState('company'); // 'company' or departmentId string
  const defaultAttendanceSettingsDraft = () => ({
    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    workStartTime: '09:00',
    workEndTime: '17:00',
    lunchBreakStart: '13:00',
    lunchBreakEnd: '14:00',
    graceTimeInMinutes: 15,
    requireApproval: false,
    requireLocationForAttendance: true,
    geofencingEnabled: true,
    geofencingRadius: 100,
    geofencingLocation: { address: '', coordinates: { lat: null, lng: null } }
  });
  const [settingsDraft, setSettingsDraft] = useState(() => defaultAttendanceSettingsDraft());
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsApiError, setSettingsApiError] = useState('');
  const [settingsIsPersisted, setSettingsIsPersisted] = useState(false);
  const [settingsLastSavedSnapshot, setSettingsLastSavedSnapshot] = useState(() => defaultAttendanceSettingsDraft());
  const [settingsSummariesLoading, setSettingsSummariesLoading] = useState(false);
  const [settingsSummaries, setSettingsSummaries] = useState({}); // key -> { exists: boolean, draft: normalizedDraft|null }
  const settingsReqRef = React.useRef(0);
  const modalRecordsRef = React.useRef(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!showExportMenu) return;
      const el = exportMenuRef.current;
      if (el && !el.contains(e.target)) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showExportMenu]);

  // Leaflet marker icon fix (same as Tasks / settings modal)
  try {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
    });
  } catch {}

  const FitBounds = ({ bounds, boundsKey, enabled = true }) => {
    const map = useMap();
    const lastKeyRef = React.useRef(null);
    useEffect(() => {
      if (!enabled) return;
      if (!bounds) return;
      if (!boundsKey) return;
      if (lastKeyRef.current === boundsKey) return;
      try {
        map.fitBounds(bounds, { padding: [24, 24] });
        lastKeyRef.current = boundsKey;
      } catch {}
    }, [map, bounds, boundsKey, enabled]);
    return null;
  };

  const FlyToPoint = ({ point }) => {
    const map = useMap();
    useEffect(() => {
      if (!point?.lat || !point?.lng) return;
      try {
        map.flyTo([point.lat, point.lng], 17, { duration: 0.8 });
      } catch {}
    }, [map, point?.lat, point?.lng]);
    return null;
  };

  const makeLabeledDivIcon = ({ label, bg = '#111827', fg = '#fff', border = 'rgba(255,255,255,0.9)' }) =>
    L.divIcon({
      className: 'wl-att-pin',
      html: `
        <div style="
          display:inline-flex;
          align-items:center;
          justify-content:center;
          height:26px;
          min-width:26px;
          padding:0 10px;
          border-radius:999px;
          background:${bg};
          color:${fg};
          border:2px solid ${border};
          box-shadow:0 10px 22px rgba(0,0,0,0.22);
          font-weight:900;
          font-size:10px;
          letter-spacing:0.6px;
          text-transform:uppercase;
          transform: translate(-50%, -100%);
          white-space:nowrap;
        ">${label}</div>
      `,
      iconSize: [26, 26],
      iconAnchor: [0, 0],
      popupAnchor: [0, -22]
    });

  const getWorkHoursClusterColor = (avgHours, colors) => {
    const h = Number(avgHours);
    if (!Number.isFinite(h)) return { bg: colors.weekend, fg: colors.textMuted };
    if (h >= 8) return { bg: colors.present, fg: '#fff' };
    if (h >= 6) return { bg: colors.today, fg: '#fff' };
    if (h >= 4) return { bg: colors.late, fg: '#fff' };
    return { bg: colors.absent, fg: '#fff' };
  };

  const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const isNonWorkingDay = (dayOfWeek, settings) => {
    const workingDays = Array.isArray(settings?.workingDays) ? settings.workingDays : ['monday','tuesday','wednesday','thursday','friday'];
    return !workingDays.includes(DAY_NAMES[dayOfWeek]);
  };

  const parseHHMMToMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const s = hhmm.length >= 5 ? hhmm.slice(0, 5) : hhmm;
    const [hh, mm] = s.split(':').map(x => Number(x));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    return hh * 60 + mm;
  };

  const isLateCheckInTime = (checkInTime, settings) => {
    if (!checkInTime) return null;
    const d = new Date(checkInTime);
    if (Number.isNaN(d.getTime())) return null;
    const startMins = parseHHMMToMinutes(settings?.workStartTime ?? '09:00');
    const grace = Number.isFinite(Number(settings?.graceTimeInMinutes)) ? Number(settings.graceTimeInMinutes) : 0;
    if (startMins == null) return null;
    const cutoff = startMins + grace;
    const actual = d.getHours() * 60 + d.getMinutes();
    return actual > cutoff;
  };

  const getLateByMinutes = (checkInTime, settings) => {
    if (!checkInTime) return null;
    const d = new Date(checkInTime);
    if (Number.isNaN(d.getTime())) return null;
    const startMins = parseHHMMToMinutes(settings?.workStartTime ?? '09:00');
    const grace = Number.isFinite(Number(settings?.graceTimeInMinutes)) ? Number(settings.graceTimeInMinutes) : 0;
    if (startMins == null) return null;
    const cutoff = startMins + grace;
    const actual = d.getHours() * 60 + d.getMinutes();
    const diff = actual - cutoff;
    return diff > 0 ? diff : 0;
  };

  const formatHHMM12 = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return hhmm || '';
    const [hh, mm] = hhmm.split(':').map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return hhmm;
    const period = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${h12}:${String(mm).padStart(2, '0')} ${period}`;
  };

  const formatLateBy = (mins) => {
    if (mins == null || !Number.isFinite(mins) || mins <= 0) return '';
    if (mins < 60) return `${Math.round(mins)}m`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h${m ? ` ${m}m` : ''}`;
  };

  const ClusteredPointsLayer = ({ points, officeCenter, icons, onPointClick }) => {
    const map = useMap();
    const groupRef = React.useRef(null);
    const officeRef = React.useRef(null);

    useEffect(() => {
      // Cleanup any previous layers
      try {
        if (groupRef.current) map.removeLayer(groupRef.current);
      } catch {}
      try {
        if (officeRef.current) map.removeLayer(officeRef.current);
      } catch {}

      // Cluster group for points
      const clusterGroup = L.markerClusterGroup({
        chunkedLoading: true,
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        zoomToBoundsOnClick: false,
        disableClusteringAtZoom: 18,
        iconCreateFunction: (cluster) => {
          try {
            const markers = cluster.getAllChildMarkers();
            const inMarkers = markers.filter(m => m?.options?.kind === 'IN' && Number.isFinite(Number(m?.options?.workHours)));
            const count = inMarkers.length;
            const sum = inMarkers.reduce((s, m) => s + Number(m.options.workHours), 0);
            const avg = count > 0 ? (sum / count) : 0;
            const { bg, fg } = getWorkHoursClusterColor(avg, colors);
            const label = `${cluster.getChildCount()}`;
            const sub = count > 0 ? `${avg.toFixed(avg < 10 ? 1 : 0)}h` : '';
            return L.divIcon({
              className: 'wl-att-cluster',
              html: `
                <div style="
                  width:46px; height:46px;
                  border-radius:999px;
                  background:${bg};
                  border:3px solid rgba(255,255,255,0.95);
                  box-shadow:0 14px 28px rgba(0,0,0,0.22);
                  display:flex;
                  flex-direction:column;
                  align-items:center;
                  justify-content:center;
                  color:${fg};
                  transform: translate(-50%, -50%);
                ">
                  <div style="font-weight:1000; font-size:14px; line-height:14px">${label}</div>
                  <div style="font-weight:900; font-size:9px; line-height:11px; opacity:0.95">${sub}</div>
                </div>
              `,
              iconSize: [46, 46],
              iconAnchor: [0, 0]
            });
          } catch {
            return null;
          }
        }
      });

      (points || []).forEach((p) => {
        const icon =
          p.kind === 'IN'
            ? (p.isLate ? icons.inLate : icons.inOnTime)
            : icons.out;

        const marker = L.marker([p.lat, p.lng], { icon, kind: p.kind, workHours: p.workHours });
        const title = `${p.kind}${p.kind === 'IN' && p.isLate != null ? (p.isLate ? ' • Late' : ' • On time') : ''}`;
        const when = p.time ? `${new Date(p.time).toLocaleDateString([], { month: 'short', day: 'numeric' })} ${new Date(p.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}` : '';
        marker.bindPopup(`
          <div style="font-size:12px">
            <div style="font-weight:900; margin-bottom:4px">${title}${when ? ` • ${when}` : ''}</div>
            <div style="color:#64748b">${p.address || `${Number(p.lat).toFixed(5)}, ${Number(p.lng).toFixed(5)}`}</div>
          </div>
        `);
        marker.on('click', () => onPointClick?.(p));
        clusterGroup.addLayer(marker);
      });

      map.addLayer(clusterGroup);
      groupRef.current = clusterGroup;

      // Office marker (kept separate so it doesn't get clustered away)
      if (officeCenter?.lat != null && officeCenter?.lng != null) {
        const office = L.marker([officeCenter.lat, officeCenter.lng], { icon: icons.office });
        office.bindPopup(`
          <div style="font-size:12px">
            <div style="font-weight:900; margin-bottom:4px">Office</div>
            <div style="color:#64748b">${Number(officeCenter.lat).toFixed(5)}, ${Number(officeCenter.lng).toFixed(5)}</div>
          </div>
        `);
        map.addLayer(office);
        officeRef.current = office;
      }

      return () => {
        try { map.removeLayer(clusterGroup); } catch {}
        try { if (officeRef.current) map.removeLayer(officeRef.current); } catch {}
      };
    }, [map, points, officeCenter?.lat, officeCenter?.lng, icons?.inLate, icons?.inOnTime, icons?.out, icons?.office, onPointClick]);

    return null;
  };

  const computeDailyLoggedHours = (records, daysInMonth) => {
    const out = {};
    const now = new Date();
    (records || []).forEach((r) => {
      if (!r?.checkInTime) return;
      const start = new Date(r.checkInTime);
      if (Number.isNaN(start.getTime())) return;
      const end = r.checkOutTime ? new Date(r.checkOutTime) : now;
      if (Number.isNaN(end.getTime())) return;
      const s = start < end ? start : end;
      const e = start < end ? end : start;

      daysInMonth.forEach((day) => {
        const dayStart = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), 0, 0, 0, 0);
        const dayEnd = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), 23, 59, 59, 999);
        const overlapStart = s > dayStart ? s : dayStart;
        const overlapEnd = e < dayEnd ? e : dayEnd;
        const ms = overlapEnd - overlapStart;
        if (ms > 0) {
          out[day.dateStr] = (out[day.dateStr] || 0) + ms / (1000 * 60 * 60);
        }
      });
    });
    return out;
  };

  // Month/Year selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Color palette for light mode
  const colors = useMemo(() => ({
    present: '#22c55e',
    presentBg: '#dcfce7',
    late: '#f59e0b',
    lateBg: '#fef3c7',
    partial: '#8b5cf6',
    partialBg: '#ede9fe',
    absent: '#ef4444',
    absentBg: '#fee2e2',
    weekend: '#f1f5f9',
    today: '#3b82f6',
    todayBg: '#dbeafe',
    border: '#e2e8f0',
    text: '#1e293b',
    textMuted: '#64748b',
    cardBg: '#ffffff',
    pageBg: '#f8fafc'
  }), []);

  const normalizeAttendanceSettingsFromApi = (settings) => {
    const d = defaultAttendanceSettingsDraft();
    const timeToInput = (t) => (typeof t === 'string' && t.length >= 5 ? t.slice(0, 5) : '');

    const geofence = settings?.geofencingLocation;
    const coords = geofence?.coordinates;
    const coordsObj = coords && typeof coords === 'object' && !Array.isArray(coords) ? coords : null;
    const lat = coordsObj?.lat ?? geofence?.lat ?? null;
    const lng = coordsObj?.lng ?? geofence?.lng ?? null;
    const address = geofence?.address ?? '';

    return {
      ...d,
      workingDays: Array.isArray(settings?.workingDays) ? settings.workingDays : d.workingDays,
      workStartTime: timeToInput(settings?.workStartTime) || d.workStartTime,
      workEndTime: timeToInput(settings?.workEndTime) || d.workEndTime,
      lunchBreakStart: timeToInput(settings?.lunchBreakStart) || d.lunchBreakStart,
      lunchBreakEnd: timeToInput(settings?.lunchBreakEnd) || d.lunchBreakEnd,
      graceTimeInMinutes: Number.isFinite(Number(settings?.graceTimeInMinutes)) ? Number(settings.graceTimeInMinutes) : d.graceTimeInMinutes,
      requireApproval: !!settings?.requireApproval,
      requireLocationForAttendance: !!settings?.requireLocationForAttendance,
      geofencingEnabled: !!settings?.geofencingEnabled,
      geofencingRadius: Number.isFinite(Number(settings?.geofencingRadius)) ? Number(settings.geofencingRadius) : d.geofencingRadius,
      geofencingLocation: {
        address,
        coordinates: { lat: lat == null ? null : Number(lat), lng: lng == null ? null : Number(lng) }
      }
    };
  };

  const loadAttendanceSettings = async (deptId) => {
    if (!company?.id) return;
    const reqId = ++settingsReqRef.current;
    setSettingsLoading(true);
    setSettingsApiError('');
    try {
      const { data } = await api.get(`/api/companies/${company.id}/attendance-settings`, {
        params: { departmentId: deptId === 'company' ? 'company' : deptId, createIfMissing: 0 }
      });
      if (reqId !== settingsReqRef.current) return;
      if (!data?.is_success) {
        setSettingsApiError(data?.message || 'Failed to load settings');
        const d = defaultAttendanceSettingsDraft();
        setSettingsIsPersisted(false);
        setSettingsDraft(d);
        setSettingsLastSavedSnapshot(d);
        return;
      }
      const exists = data?.exists != null ? !!data.exists : !!data.settings;
      if (!exists || !data.settings) {
        const d = defaultAttendanceSettingsDraft();
        setSettingsIsPersisted(false);
        setSettingsDraft(d);
        setSettingsLastSavedSnapshot(d);
        return;
      }
      const normalized = normalizeAttendanceSettingsFromApi(data.settings);
      setSettingsIsPersisted(true);
      setSettingsDraft(normalized);
      setSettingsLastSavedSnapshot(normalized);
    } catch (e) {
      if (reqId !== settingsReqRef.current) return;
      setSettingsApiError(e?.response?.data?.message || 'Failed to load settings');
      const d = defaultAttendanceSettingsDraft();
      setSettingsIsPersisted(false);
      setSettingsDraft(d);
      setSettingsLastSavedSnapshot(d);
    } finally {
      if (reqId === settingsReqRef.current) setSettingsLoading(false);
    }
  };

  const loadAttendanceSettingsSummaries = async () => {
    if (!company?.id) return;
    setSettingsSummariesLoading(true);
    try {
      const keys = ['company', ...(departments || []).map(d => String(d.id))];
      const results = await Promise.all(keys.map(async (k) => {
        try {
          const { data } = await api.get(`/api/companies/${company.id}/attendance-settings`, {
            params: { departmentId: k === 'company' ? 'company' : k, createIfMissing: 0 }
          });
          const exists = data?.exists != null ? !!data.exists : !!data?.settings;
          const draft = (exists && data?.settings) ? normalizeAttendanceSettingsFromApi(data.settings) : null;
          return [k, { exists, draft }];
        } catch {
          return [k, { exists: false, draft: null }];
        }
      }));
      const next = {};
      results.forEach(([k, v]) => { next[k] = v; });
      setSettingsSummaries(next);
    } finally {
      setSettingsSummariesLoading(false);
    }
  };

  // Helper: pick an employee's primary department (used when an employee belongs to multiple departments)
  const getEmployeePrimaryDepartment = (emp) => {
    const depts = emp?.departments || [];
    if (!Array.isArray(depts) || depts.length === 0) return null;

    // Common patterns across APIs / joins
    const explicitId =
      emp?.primaryDepartmentId ??
      emp?.primaryDeptId ??
      emp?.departmentId;
    if (explicitId != null) {
      const found = depts.find(d => String(d.id) === String(explicitId));
      if (found) return found;
    }

    const flagged =
      depts.find(d => d?.isPrimary === true) ||
      depts.find(d => d?.EmployeeDepartment?.isPrimary === true) ||
      depts.find(d => d?.employeeDepartment?.isPrimary === true) ||
      depts.find(d => d?.UserDepartment?.isPrimary === true) ||
      depts.find(d => d?.userDepartment?.isPrimary === true);
    if (flagged) return flagged;

    return depts[0] || null;
  };

  const getEffectiveAttendanceSettingsForEmployee = (emp) => {
    const primaryDept = getEmployeePrimaryDepartment(emp);
    const primaryKey = primaryDept?.id != null ? String(primaryDept.id) : null;

    const deptSettings = primaryKey ? settingsSummaries?.[primaryKey]?.draft : null;
    if (deptSettings) return { settings: deptSettings, source: { type: 'department', department: primaryDept } };

    const companySettings = settingsSummaries?.company?.draft;
    if (companySettings) return { settings: companySettings, source: { type: 'company' } };

    // Fall back to defaults (not persisted / no server data)
    return { settings: defaultAttendanceSettingsDraft(), source: { type: 'default' } };
  };

  const getOfficeCenterFromSettings = (settings) => {
    const lat = settings?.geofencingLocation?.coordinates?.lat;
    const lng = settings?.geofencingLocation?.coordinates?.lng;
    if (lat == null || lng == null) return null;
    const nLat = Number(lat);
    const nLng = Number(lng);
    if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) return null;
    return { lat: nLat, lng: nLng };
  };

  const getEmployeeLocationStats = (emp, records) => {
    const { settings, source } = getEffectiveAttendanceSettingsForEmployee(emp);
    const requireLocation = settings?.requireLocationForAttendance !== undefined ? !!settings.requireLocationForAttendance : true;
    const geofencingEnabled = !!settings?.geofencingEnabled;
    const radiusM = Number.isFinite(Number(settings?.geofencingRadius)) ? Number(settings.geofencingRadius) : null;
    const officeCenter = geofencingEnabled ? getOfficeCenterFromSettings(settings) : null;

    let expected = 0;
    let captured = 0;
    let missing = 0;

    let distancesKm = [];
    let inside = 0;
    let outside = 0;

    (records || []).forEach(r => {
      // Always expect check-in location if requiring location
      const expectsIn = requireLocation;
      const expectsOut = requireLocation && !!r?.checkOutTime;
      if (expectsIn) expected += 1;
      if (expectsOut) expected += 1;

      const inLoc = getLatLngFromLocation(r?.checkInLocation);
      const outLoc = getLatLngFromLocation(r?.checkOutLocation);

      if (expectsIn) {
        if (inLoc) captured += 1;
        else missing += 1;
      }
      if (expectsOut) {
        if (outLoc) captured += 1;
        else missing += 1;
      }

      if (officeCenter && radiusM != null) {
        if (inLoc) {
          const km = haversineKm(officeCenter, inLoc);
          if (Number.isFinite(km)) distancesKm.push(km);
          if (Number.isFinite(km)) (km * 1000 <= radiusM ? inside++ : outside++);
        }
        if (outLoc) {
          const km = haversineKm(officeCenter, outLoc);
          if (Number.isFinite(km)) distancesKm.push(km);
          if (Number.isFinite(km)) (km * 1000 <= radiusM ? inside++ : outside++);
        }
      }
    });

    const captureRate = expected > 0 ? Math.round((captured / expected) * 100) : null;
    const avgKm = distancesKm.length ? (distancesKm.reduce((s, x) => s + x, 0) / distancesKm.length) : null;

    return {
      source,
      requireLocation,
      expected,
      captured,
      missing,
      captureRate,
      geofencingEnabled: !!officeCenter && radiusM != null,
      radiusM: radiusM != null ? radiusM : null,
      avgKm,
      inside,
      outside
    };
  };

  const saveAttendanceSettings = async (deptId) => {
    if (!company?.id) return;
    setSettingsSaving(true);
    setSettingsApiError('');
    try {
      const payload = {
        ...settingsDraft,
        // backend expects geofencingLocation JSON; keep our shape
        geofencingLocation: settingsDraft.geofencingLocation
      };
      const { data } = await api.put(`/api/companies/${company.id}/attendance-settings`, payload, {
        params: { departmentId: deptId === 'company' ? 'company' : deptId }
      });
      if (!data?.is_success) {
        setSettingsApiError(data?.message || 'Failed to save settings');
        return;
      }
      const normalized = normalizeAttendanceSettingsFromApi(data.settings);
      setSettingsIsPersisted(true);
      setSettingsDraft(normalized);
      setSettingsLastSavedSnapshot(normalized);
      // Update summaries for current selection
      setSettingsSummaries(prev => ({ ...prev, [deptId]: { exists: true, draft: normalized } }));
    } catch (e) {
      setSettingsApiError(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Generate date range for selected month
  const dateRange = useMemo(() => {
    const startDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const endDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  }, [selectedMonth, selectedYear]);

  // Generate days of month
  const daysInMonth = useMemo(() => {
    const lastDay = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const days = [];
    for (let d = 1; d <= lastDay; d++) {
      const date = new Date(selectedYear, selectedMonth, d);
      days.push({
        day: d,
        date,
        dayOfWeek: date.getDay(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.toDateString() === new Date().toDateString(),
        dateStr: date.toDateString()
      });
    }
    return days;
  }, [selectedMonth, selectedYear]);

  // Fetch all employees and supervisors (only once per company)
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !company?.id) return;

      try {
        const [employeesRes, supervisorsRes, departmentsRes] = await Promise.all([
          api.get(`/api/companies/${company.id}/employees`),
          api.get('/api/companies/users'),
          api.get(`/api/departments/${company.id}`)
        ]);
        
        if (employeesRes.data?.is_success) {
          setAllEmployees(employeesRes.data.employees || []);
        }
        
        if (supervisorsRes.data?.is_success) {
          // Filter to only active supervisors
          const activeSupervisors = (supervisorsRes.data.users || []).filter(u => 
            u.isSupervisor && (u.status === 'active' || u.isActive !== false)
          );
          setAllSupervisors(activeSupervisors);
        }

        if (departmentsRes.data?.is_success) {
          setDepartments(departmentsRes.data.departments || []);
        }
      } catch (err) {
        console.error('Data fetch error:', err);
      }
    };

    fetchData();
  }, [isAuthenticated, company?.id]);

  // Fetch attendance data
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!isAuthenticated || !company?.id) return;

      setLoading(true);
      setError(null);

      try {
        const { data } = await api.get('/api/attendance/employees', {
          params: { startDate: dateRange.startDate, endDate: dateRange.endDate }
        });

        if (data?.is_success) {
          setAttendanceData(data.attendance || []);
        } else {
          setError(data?.message || 'Failed to fetch attendance data');
          setAttendanceData([]);
        }
      } catch (err) {
        console.error('Attendance fetch error:', err);
        setError(err?.response?.data?.message || 'Failed to fetch attendance data');
        setAttendanceData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [isAuthenticated, company?.id, dateRange.startDate, dateRange.endDate, refreshTrigger]);

  // Preload settings summaries so we can compute location/geofence stats for employees (not only inside the settings modal)
  useEffect(() => {
    if (!company?.id) return;
    // Wait until departments list is loaded (can be empty; still load company default)
    loadAttendanceSettingsSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, departments?.length]);

  // Load attendance settings from backend when settings modal opens or department changes
  useEffect(() => {
    if (!showSettings) return;
    if (!company?.id) return;
    loadAttendanceSettings(settingsDepartmentId);
    loadAttendanceSettingsSummaries();
  }, [showSettings, company?.id, settingsDepartmentId]);

  const settingsIsDirty = useMemo(() => {
    try {
      return JSON.stringify(settingsDraft) !== JSON.stringify(settingsLastSavedSnapshot);
    } catch {
      return true;
    }
  }, [settingsDraft, settingsLastSavedSnapshot]);

  // Check if checkout is on a different day than checkin (defined early for use in attendanceMap)
  const getDaysDifference = (checkInTime, checkOutTime) => {
    if (!checkInTime || !checkOutTime) return 0;
    const checkIn = new Date(checkInTime);
    const checkOut = new Date(checkOutTime);
    // Reset to start of day for comparison
    const checkInDay = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
    const checkOutDay = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
    const diffTime = checkOutDay - checkInDay;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Process attendance into a lookup map with continuation tracking
  const attendanceMap = useMemo(() => {
    const map = {};
    attendanceData.forEach(emp => {
      const empId = emp.employee?.id;
      if (!empId) return;
      map[empId] = {};
      
      (emp.records || []).forEach(record => {
        const checkInDate = new Date(record.checkInTime);
        const dateStr = checkInDate.toDateString();
        
        // Mark the check-in day
        map[empId][dateStr] = { 
          ...record, 
          isCheckInDay: true,
          isCheckOutDay: false,
          isContinuationDay: false
        };
        
        // If checkout is on a different day, mark continuation days
        if (record.checkOutTime) {
          const checkOutDate = new Date(record.checkOutTime);
          const daysDiff = getDaysDifference(record.checkInTime, record.checkOutTime);
          
          if (daysDiff > 0) {
            // Mark the checkout day
            const checkOutDateStr = checkOutDate.toDateString();
            
            // Mark intermediate days as continuation
            const currentDate = new Date(checkInDate);
            currentDate.setDate(currentDate.getDate() + 1);
            
            while (currentDate <= checkOutDate) {
              const currDateStr = currentDate.toDateString();
              const isCheckOutDay = currDateStr === checkOutDateStr;
              
              // Only add if not already a check-in day for another record
              if (!map[empId][currDateStr] || !map[empId][currDateStr].isCheckInDay) {
                map[empId][currDateStr] = {
                  ...record,
                  isCheckInDay: false,
                  isCheckOutDay: isCheckOutDay,
                  isContinuationDay: !isCheckOutDay,
                  originalCheckIn: record.checkInTime
                };
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
          }
        }
      });
    });
    return map;
  }, [attendanceData]);

  // Merge all employees with attendance data - show all employees even without attendance
  const mergedEmployees = useMemo(() => {
    // Create a map of employees with attendance data by their ID
    const attendanceByEmpId = {};
    attendanceData.forEach(emp => {
      const empId = emp.employee?.id;
      if (empId) {
        attendanceByEmpId[empId] = emp;
      }
    });

    // Start with all employees from the company - always use full employee data
    const merged = allEmployees.map(emp => {
      const attendanceRecord = attendanceByEmpId[emp.id];
      // Merge employee data from allEmployees (has departments/supervisors) 
      // with face recognition data from attendance response (has enrolledFaceUrl)
      const attendanceEmployee = attendanceRecord?.employee || {};
      return {
        employee: {
          ...emp, // Full employee data including departments & supervisors
          // Add face recognition fields from attendance response
          faceEnrolled: attendanceEmployee.faceEnrolled ?? emp.faceEnrolled,
          faceEnrolledAt: attendanceEmployee.faceEnrolledAt ?? emp.faceEnrolledAt,
          faceImageS3Key: attendanceEmployee.faceImageS3Key ?? emp.faceImageS3Key,
          enrolledFaceUrl: attendanceEmployee.enrolledFaceUrl, // Only from attendance response
          // Re-enrollment pending fields
          faceReEnrollmentPending: attendanceEmployee.faceReEnrollmentPending ?? emp.faceReEnrollmentPending,
          faceReEnrollmentRequestedAt: attendanceEmployee.faceReEnrollmentRequestedAt ?? emp.faceReEnrollmentRequestedAt
        },
        records: attendanceRecord?.records || []
      };
    });

    // Sort by name
    merged.sort((a, b) => {
      const nameA = `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.toLowerCase();
      const nameB = `${b.employee?.firstName || ''} ${b.employee?.lastName || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return merged;
  }, [allEmployees, attendanceData]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const companySettings = settingsSummaries?.company?.draft;
    const workingDays = daysInMonth.filter(d => !isNonWorkingDay(d.dayOfWeek, companySettings)).length;
    let totalPresent = 0;
    let totalLate = 0;
    let totalHours = 0;
    let overtimeHours = 0;
    let remoteCheckIns = 0;
    let remoteEligible = 0;
    let gpsExpected = 0;
    let gpsMissing = 0;

    const getLatLng = (loc) => {
      if (!loc) return null;
      const coords = loc.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        const lng = Number(coords[0]);
        const lat = Number(coords[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      }
      const lat = Number(loc.lat ?? loc.latitude);
      const lng = Number(loc.lng ?? loc.lon ?? loc.long ?? loc.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      return null;
    };

    const haversineKmLocal = (a, b) => {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
    };

    const scheduledHoursFromSettings = (settings) => {
      const startM = parseHHMMToMinutes(settings?.workStartTime ?? '09:00');
      const endM = parseHHMMToMinutes(settings?.workEndTime ?? '17:00');
      if (startM == null || endM == null) return null;
      let mins = endM - startM;
      if (mins <= 0) return null;
      const lunchStart = parseHHMMToMinutes(settings?.lunchBreakStart);
      const lunchEnd = parseHHMMToMinutes(settings?.lunchBreakEnd);
      if (lunchStart != null && lunchEnd != null && lunchEnd > lunchStart) {
        mins -= (lunchEnd - lunchStart);
      }
      return mins > 0 ? mins / 60 : null;
    };

    mergedEmployees.forEach(emp => {
      const effective = getEffectiveAttendanceSettingsForEmployee(emp.employee);
      const settings = effective?.settings;
      const scheduledH = scheduledHoursFromSettings(settings);
      const requireLoc = settings?.requireLocationForAttendance !== undefined ? !!settings.requireLocationForAttendance : true;
      const radiusM = Number.isFinite(Number(settings?.geofencingRadius)) ? Number(settings.geofencingRadius) : null;
      const officeCenter = settings?.geofencingEnabled ? getOfficeCenterFromSettings(settings) : null;

      (emp.records || []).forEach(record => {
        totalPresent++;
        const late = isLateCheckInTime(record.checkInTime, settings);
        if (late === true) totalLate++;
        const hrs = parseFloat(record.totalHours) || 0;
        totalHours += hrs;

        if (scheduledH != null) {
          overtimeHours += Math.max(0, hrs - scheduledH);
        }

        const inLoc = getLatLng(record.checkInLocation);
        const outLoc = getLatLng(record.checkOutLocation);

        // GPS compliance (when required)
        if (requireLoc) {
          gpsExpected += 1;
          if (!inLoc) gpsMissing += 1;
          if (record.checkOutTime) {
            gpsExpected += 1;
            if (!outLoc) gpsMissing += 1;
          }
        }

        // Remote check-ins (only if geofence configured)
        if (officeCenter && radiusM != null && inLoc) {
          remoteEligible += 1;
          const km = haversineKmLocal(officeCenter, inLoc);
          if (Number.isFinite(km) && km * 1000 > radiusM) remoteCheckIns += 1;
        }
      });
    });

    return {
      totalEmployees: mergedEmployees.length,
      workingDays,
      totalRecords: totalPresent,
      avgAttendance: mergedEmployees.length > 0 && workingDays > 0 
        ? ((totalPresent / (mergedEmployees.length * workingDays)) * 100).toFixed(0) 
        : 0,
      onTimeRate: totalPresent > 0 ? (((totalPresent - totalLate) / totalPresent) * 100).toFixed(0) : 0,
      totalHours: totalHours.toFixed(0),
      overtimeHours: overtimeHours.toFixed(1),
      remoteCheckIns,
      remoteEligible,
      remoteRate: remoteEligible > 0 ? Math.round((remoteCheckIns / remoteEligible) * 100) : null,
      gpsExpected,
      gpsMissing,
      gpsCaptureRate: gpsExpected > 0 ? Math.round(((gpsExpected - gpsMissing) / gpsExpected) * 100) : null
    };
  }, [mergedEmployees, daysInMonth, settingsSummaries]);

  // Navigation handlers
  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToToday = () => {
    setSelectedMonth(new Date().getMonth());
    setSelectedYear(new Date().getFullYear());
  };

  // Get cell content for employee/day
  const getCellContent = (empId, day, empSettings) => {
    const record = attendanceMap[empId]?.[day.dateStr];
    const nonWorking = isNonWorkingDay(day.dayOfWeek, empSettings);
    if (!record) {
      if (nonWorking) return { type: 'weekend', icon: null, continues: false };
      return { type: 'absent', icon: <FiX size={10} />, continues: false };
    }

    const daysDiff = getDaysDifference(record.checkInTime, record.checkOutTime);
    const continues = daysDiff > 0 && record.isCheckInDay;
    
    // Continuation day (middle of a multi-day session)
    if (record.isContinuationDay) {
      return { 
        type: 'continuation', 
        icon: <FiArrowRight size={10} />, 
        record, 
        continues: true, 
        daysDiff,
        checkInTime: null,
        checkOutTime: null
      };
    }
    
    // Checkout day (end of a multi-day session)
    if (record.isCheckOutDay && !record.isCheckInDay) {
      return { 
        type: 'checkout', 
        icon: null, 
        record, 
        continues: false, 
        daysDiff,
        checkInTime: null,
        checkOutTime: formatTime(record.checkOutTime)
      };
    }

    const status = record.status?.toLowerCase() || 'present';
    const isLate = isLateCheckInTime(record.checkInTime, empSettings) === true;
    const checkInTimeStr = formatTime(record.checkInTime);
    const checkOutTimeStr = record.checkOutTime && !continues ? formatTime(record.checkOutTime) : null;
    
    if (status === 'partial') return { type: 'partial', icon: <FiMinus size={10} />, record, continues, daysDiff, checkInTime: checkInTimeStr, checkOutTime: checkOutTimeStr };
    if (isLate) return { type: 'late', icon: <FiAlertCircle size={10} />, record, continues, daysDiff, checkInTime: checkInTimeStr, checkOutTime: checkOutTimeStr };
    return { type: 'present', icon: <FiCheck size={10} />, record, continues, daysDiff, checkInTime: checkInTimeStr, checkOutTime: checkOutTimeStr };
  };

  // Format helpers
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDateYMD = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const computeBreaksSummary = (breaks) => {
    const arr = Array.isArray(breaks) ? breaks : [];
    let count = arr.length;
    let hours = 0;
    arr.forEach((b) => {
      const d = parseFloat(b?.duration);
      if (Number.isFinite(d)) {
        hours += d;
        return;
      }
      const s = b?.startTime ? new Date(b.startTime) : null;
      const e = b?.endTime ? new Date(b.endTime) : null;
      if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e > s) {
        hours += (e - s) / (1000 * 60 * 60);
      }
    });
    return { count, hours: Number(hours.toFixed(2)) };
  };

  const buildExportRows = () => {
    const rows = [];
    const byEmployee = {};

    mergedEmployees.forEach((empWrap) => {
      const emp = empWrap.employee || {};
      const records = empWrap.records || [];
      const primaryDept = getEmployeePrimaryDepartment(emp);
      const effective = getEffectiveAttendanceSettingsForEmployee(emp);
      const settings = effective?.settings;
      const officeCenter = settings?.geofencingEnabled ? getOfficeCenterFromSettings(settings) : null;
      const radiusM = Number.isFinite(Number(settings?.geofencingRadius)) ? Number(settings.geofencingRadius) : null;
      const officeAddr = settings?.geofencingLocation?.address || '';

      const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
      const sheetKey = `${empName}__${emp.email || ''}`;
      if (!byEmployee[sheetKey]) byEmployee[sheetKey] = { name: empName, email: emp.email || '', rows: [] };

      records.forEach((r) => {
        const totalH = Number.isFinite(Number(r?.totalHours)) ? Number(r.totalHours) : 0;
        const lateBy = getLateByMinutes(r?.checkInTime, settings);
        const isLate = isLateCheckInTime(r?.checkInTime, settings) === true;

        const scheduledH = (() => {
          const startM = parseHHMMToMinutes(settings?.workStartTime ?? '09:00');
          const endM = parseHHMMToMinutes(settings?.workEndTime ?? '17:00');
          if (startM == null || endM == null) return null;
          let mins = endM - startM;
          if (mins <= 0) return null;
          const lunchStart = parseHHMMToMinutes(settings?.lunchBreakStart);
          const lunchEnd = parseHHMMToMinutes(settings?.lunchBreakEnd);
          if (lunchStart != null && lunchEnd != null && lunchEnd > lunchStart) mins -= (lunchEnd - lunchStart);
          return mins > 0 ? mins / 60 : null;
        })();
        const overtimeH = scheduledH != null ? Math.max(0, totalH - scheduledH) : null;

        const inLL = getLatLngFromLocation(r?.checkInLocation);
        const outLL = getLatLngFromLocation(r?.checkOutLocation);
        const inDistM = (officeCenter && radiusM != null && inLL) ? (haversineKm(officeCenter, inLL) * 1000) : null;
        const outDistM = (officeCenter && radiusM != null && outLL) ? (haversineKm(officeCenter, outLL) * 1000) : null;
        const inInside = (inDistM != null && radiusM != null) ? (inDistM <= radiusM) : null;
        const outInside = (outDistM != null && radiusM != null) ? (outDistM <= radiusM) : null;
        const isRemote = inInside === false;

        const breaks = computeBreaksSummary(r?.breaks);

        const row = {
          'Employee Name': empName,
          'Email': emp.email || '',
          'Primary Department': primaryDept?.name || '',
          'Check-in Date': formatDateYMD(r?.checkInTime),
          'Check-in Time': formatTime(r?.checkInTime),
          'Check-out Date': formatDateYMD(r?.checkOutTime),
          'Check-out Time': r?.checkOutTime ? formatTime(r.checkOutTime) : '',
          'Total Hours': totalH ? Number(totalH.toFixed(2)) : 0,
          'Breaks': breaks.count,
          'Break Hours': breaks.hours,
          'On Time?': isLate ? 'No' : 'Yes',
          'Late By': isLate ? (formatLateBy(lateBy) || '') : '',
          'Overtime Hours': overtimeH != null ? Number(overtimeH.toFixed(2)) : '',
          'Remote?': isRemote === true ? 'Yes' : (isRemote === false ? 'No' : ''),
          'Check-in Address': r?.checkInLocation?.address || '',
          'Check-in Lat': inLL ? inLL.lat : '',
          'Check-in Lng': inLL ? inLL.lng : '',
          'Check-out Address': r?.checkOutLocation?.address || '',
          'Check-out Lat': outLL ? outLL.lat : '',
          'Check-out Lng': outLL ? outLL.lng : '',
          'Office Address': officeAddr,
          'Office Lat': officeCenter ? officeCenter.lat : '',
          'Office Lng': officeCenter ? officeCenter.lng : '',
          'Geofence Radius (m)': radiusM != null ? radiusM : '',
          'Check-in Distance (m)': inDistM != null ? Math.round(inDistM) : '',
          'Check-in Inside Fence?': inInside === true ? 'Yes' : (inInside === false ? 'No' : ''),
          'Check-out Distance (m)': outDistM != null ? Math.round(outDistM) : '',
          'Check-out Inside Fence?': outInside === true ? 'Yes' : (outInside === false ? 'No' : '')
        };

        rows.push(row);
        byEmployee[sheetKey].rows.push(row);
      });
    });

    return { masterRows: rows, byEmployee };
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const exportCSV = () => {
    const { masterRows } = buildExportRows();
    const cols = masterRows[0] ? Object.keys(masterRows[0]) : [];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [
      cols.join(','),
      ...masterRows.map(r => cols.map(c => escape(r[c])).join(','))
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `attendance-master-${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}.csv`);
  };

  const sanitizeSheetName = (name, used) => {
    const base = (name || 'Sheet').replace(/[\[\]\:\*\?\/\\]/g, '').trim() || 'Sheet';
    let n = base.slice(0, 31);
    let i = 2;
    while (used.has(n)) {
      const suffix = ` (${i++})`;
      n = (base.slice(0, 31 - suffix.length) + suffix).slice(0, 31);
    }
    used.add(n);
    return n;
  };

  const exportXLSX = () => {
    const { masterRows, byEmployee } = buildExportRows();
    const cols = masterRows[0] ? Object.keys(masterRows[0]) : [];
    const wb = XLSX.utils.book_new();

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '334155' } },
      alignment: { vertical: 'center', horizontal: 'center', wrapText: true }
    };
    const yesStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'DCFCE7' } }, font: { color: { rgb: '166534' }, bold: true } };
    const noStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'FEE2E2' } }, font: { color: { rgb: '991B1B' }, bold: true } };
    const warnStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'FEF3C7' } }, font: { color: { rgb: '92400E' }, bold: true } };

    const makeSheet = (rows) => {
      const aoa = [cols, ...(rows || []).map(r => cols.map(c => r[c]))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // column widths
      ws['!cols'] = cols.map((c) => ({ wch: Math.min(40, Math.max(12, c.length + 2)) }));

      // header style
      cols.forEach((_, idx) => {
        const cell = XLSX.utils.encode_cell({ r: 0, c: idx });
        if (ws[cell]) ws[cell].s = headerStyle;
      });

      // conditional styles
      const colIndex = (name) => cols.indexOf(name);
      const idxOnTime = colIndex('On Time?');
      const idxLateBy = colIndex('Late By');
      const idxInInside = colIndex('Check-in Inside Fence?');
      const idxRemote = colIndex('Remote?');
      const idxOvertime = colIndex('Overtime Hours');

      for (let r = 1; r <= (rows || []).length; r++) {
        const getCell = (cIdx) => XLSX.utils.encode_cell({ r, c: cIdx });
        if (idxOnTime >= 0) {
          const cell = getCell(idxOnTime);
          const v = ws[cell]?.v;
          if (v === 'Yes') ws[cell].s = yesStyle;
          if (v === 'No') ws[cell].s = noStyle;
        }
        if (idxLateBy >= 0) {
          const cell = getCell(idxLateBy);
          const v = ws[cell]?.v;
          if (v) ws[cell].s = warnStyle;
        }
        if (idxInInside >= 0) {
          const cell = getCell(idxInInside);
          const v = ws[cell]?.v;
          if (v === 'Yes') ws[cell].s = yesStyle;
          if (v === 'No') ws[cell].s = noStyle;
        }
        if (idxRemote >= 0) {
          const cell = getCell(idxRemote);
          const v = ws[cell]?.v;
          if (v === 'Yes') ws[cell].s = warnStyle;
        }
        if (idxOvertime >= 0) {
          const cell = getCell(idxOvertime);
          const v = Number(ws[cell]?.v);
          if (Number.isFinite(v) && v > 0) ws[cell].s = warnStyle;
        }
      }

      return ws;
    };

    // Master sheet
    XLSX.utils.book_append_sheet(wb, makeSheet(masterRows), 'Master');

    // Per-employee sheets
    const usedNames = new Set(['Master']);
    Object.values(byEmployee).forEach((emp) => {
      const sheetName = sanitizeSheetName(emp.name || 'Employee', usedNames);
      XLSX.utils.book_append_sheet(wb, makeSheet(emp.rows), sheetName);
    });

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    downloadBlob(new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `attendance-${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}.xlsx`
    );
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Format the time range with continuation indicator
  const formatTimeRange = (record) => {
    if (!record?.checkInTime) return '-';
    
    const checkInTime = formatTime(record.checkInTime);
    if (!record.checkOutTime) {
      return checkInTime;
    }

    const checkOutTime = formatTime(record.checkOutTime);
    const daysDiff = getDaysDifference(record.checkInTime, record.checkOutTime);

    if (daysDiff === 0) {
      return `${checkInTime} - ${checkOutTime}`;
    } else if (daysDiff === 1) {
      return `${checkInTime} - ${checkOutTime} (+1 day)`;
    } else {
      return `${checkInTime} - ${checkOutTime} (+${daysDiff} days)`;
    }
  };

  const getAvatarColor = (index) => {
    // Keep employee profile visuals neutral/grey-ish (avoid bright accent colors here).
    const colorList = ['#0f172a', '#1f2937', '#334155', '#475569', '#64748b', '#6b7280', '#94a3b8', '#111827'];
    return colorList[index % colorList.length];
  };

  // Stable key to identify an attendance "log"/session across its IN/continuation/OUT days
  const getLogGroupKey = (empId, record) => {
    if (!empId || !record) return null;
    if (record.id != null) return `${empId}-${record.id}`;
    const start = record.originalCheckIn || record.checkInTime || '';
    const end = record.checkOutTime || '';
    return `${empId}-${start}-${end}`;
  };

  const getLatLngFromLocation = (loc) => {
    if (!loc) return null;
    // Attendance locations are stored as GeoJSON-ish: { coordinates: [lng, lat], address?: string }
    const coords = loc.coordinates;
    if (Array.isArray(coords) && coords.length >= 2) {
      const lng = Number(coords[0]);
      const lat = Number(coords[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    // Fallback support (if structure ever changes)
    const lat = Number(loc.lat ?? loc.latitude);
    const lng = Number(loc.lng ?? loc.lon ?? loc.long ?? loc.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  };

  // NOTE: distance-to-office is computed from AttendanceSettings geofence location,
  // not company profile fields, because settings can vary per department.

  const haversineKm = (a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  };

  const formatDistance = (km) => {
    if (km == null || !Number.isFinite(km)) return '';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(km < 10 ? 1 : 0)} km`;
  };

  const getOsmEmbedUrl = (lat, lng) => {
    const d = 0.01; // ~1km-ish window
    const left = lng - d;
    const right = lng + d;
    const top = lat + d;
    const bottom = lat - d;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${lat}%2C${lng}`;
  };

  // Settings modal extracted to `src/components/AttendanceSettingsModal.js`

  // Auto-scroll to selected date when modal opens
  React.useEffect(() => {
    if (selectedEmployee && selectedDate && modalRecordsRef.current) {
      const targetElement = modalRecordsRef.current.querySelector(`[data-date="${selectedDate}"]`);
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [selectedEmployee, selectedDate]);

  // Clear map focus when switching employees
  React.useEffect(() => {
    if (selectedEmployee) setFocusedMapPoint(null);
  }, [selectedEmployee?.employee?.id]);

  // Employee detail modal
  const renderEmployeeModal = () => {
    if (!selectedEmployee) return null;

    const emp = selectedEmployee.employee || {};
    const records = selectedEmployee.records || [];
    const empId = emp.id;
    const primaryDept = getEmployeePrimaryDepartment(emp);
    const locationStats = getEmployeeLocationStats(emp, records);
    const effectiveResult = getEffectiveAttendanceSettingsForEmployee(emp);
    const effectiveSettings = effectiveResult?.settings;
    const effectiveSource = effectiveResult?.source;
    const officeCenter = locationStats?.geofencingEnabled ? getOfficeCenterFromSettings(effectiveSettings) : null;
    const consolidatedPoints = (() => {
      const pts = [];
      (records || []).forEach((r) => {
        const inLL = getLatLngFromLocation(r?.checkInLocation);
        const outLL = getLatLngFromLocation(r?.checkOutLocation);
        const isLate = isLateCheckInTime(r?.checkInTime, effectiveSettings);
        const workHours = Number.isFinite(Number(r?.totalHours)) ? Number(r.totalHours) : 0;
        const groupKey = getLogGroupKey(empId, r);
        if (inLL) {
          pts.push({
            key: `in-${r.id ?? r.checkInTime ?? Math.random()}`,
            kind: 'IN',
            lat: inLL.lat,
            lng: inLL.lng,
            time: r?.checkInTime,
            isLate,
            workHours,
            groupKey,
            address: r?.checkInLocation?.address || null
          });
        }
        if (outLL) {
          pts.push({
            key: `out-${r.id ?? r.checkOutTime ?? Math.random()}`,
            kind: 'OUT',
            lat: outLL.lat,
            lng: outLL.lng,
            time: r?.checkOutTime,
            workHours,
            groupKey,
            address: r?.checkOutLocation?.address || null
          });
        }
      });
      return pts;
    })();
    const consolidatedBounds = consolidatedPoints.length
      ? L.latLngBounds(consolidatedPoints.map(p => [p.lat, p.lng]))
      : null;
    const consolidatedBoundsKey = consolidatedBounds
      ? `${consolidatedBounds.getSouthWest().lat.toFixed(5)}:${consolidatedBounds.getSouthWest().lng.toFixed(5)}:${consolidatedBounds.getNorthEast().lat.toFixed(5)}:${consolidatedBounds.getNorthEast().lng.toFixed(5)}:${mapResetTick}`
      : null;
    const iconOffice = makeLabeledDivIcon({ label: 'OFFICE', bg: colors.today, fg: '#fff', border: 'rgba(255,255,255,0.95)' });
    const iconInOnTime = makeLabeledDivIcon({ label: 'IN', bg: colors.present, fg: '#fff', border: 'rgba(255,255,255,0.95)' });
    const iconInLate = makeLabeledDivIcon({ label: 'LATE', bg: colors.late, fg: '#fff', border: 'rgba(255,255,255,0.95)' });
    const iconOut = makeLabeledDivIcon({ label: 'OUT', bg: colors.partial, fg: '#fff', border: 'rgba(255,255,255,0.95)' });
    const initials = `${emp.firstName?.charAt(0) || ''}${emp.lastName?.charAt(0) || ''}`.toUpperCase() || '?';
    const totalHours = records.reduce((sum, r) => sum + (parseFloat(r.totalHours) || 0), 0);
    const workingDays = daysInMonth.filter(d => !isNonWorkingDay(d.dayOfWeek, effectiveSettings)).length;
    const attendanceRate = workingDays > 0 ? ((records.length / workingDays) * 100).toFixed(0) : 0;
    
    // Calculate on-time rate (based on settings start time + grace)
    const onTimeCount = records.filter(r => isLateCheckInTime(r.checkInTime, effectiveSettings) === false).length;
    const onTimeRate = records.length > 0 ? ((onTimeCount / records.length) * 100).toFixed(0) : 0;
    const dailyHoursMap = computeDailyLoggedHours(records, daysInMonth);
    const dailyHoursSeries = daysInMonth.map(d => ({ ...d, hours: dailyHoursMap[d.dateStr] || 0 }));
    const maxDailyHours = Math.max(1, ...dailyHoursSeries.map(x => x.hours || 0));

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'flex-end',
          zIndex: 1000,
          padding: 16
        }}
        onClick={() => { setSelectedEmployee(null); setSelectedDate(null); setSelectedLogKey(null); setExpandedMaps({}); setFocusedMapPoint(null); setShowEnrolledFace(false); }}
      >
        <div
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: 20,
            maxWidth: 1320,
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div style={{
            background: `linear-gradient(155deg, ${getAvatarColor(selectedEmployee.index || 0)} 20%, ${getAvatarColor((selectedEmployee.index || 0) + 3)} 95%)`,
            padding: '20px 24px 28px',
            position: 'relative'
          }}>
            <div className="emp-header-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              {/* Profile picture or initials */}
              <div style={{ position: 'relative' }}>
                {emp.profilePicture ? (
                  <img
                    src={emp.profilePicture}
                    alt={`${emp.firstName} ${emp.lastName}`}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 16,
                      objectFit: 'cover',
                      border: '2px solid rgba(255,255,255,0.5)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 22,
                    border: '2px solid rgba(255,255,255,0.3)'
                  }}>
                    {initials}
                  </div>
                )}
               
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.92)', textShadow: '0 1px 2px rgba(0,0,0,0.18)' }}>
                  {emp.firstName || 'Unknown'} {emp.lastName || ''}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.78)', marginTop: 4 }}>
                  {emp.email || emp.role || 'Employee'}
                </div>
                {/* Department & Supervisor inline */}
                {(() => {
                  // Get employee's departments
                  const departments = emp.departments || [];
                  // Get supervisors for this employee's departments
                  const empDeptIds = departments.map(d => d.id);
                  const supervisorsForEmp = allSupervisors.filter(sup => 
                    sup.departments?.some(d => empDeptIds.includes(d.id))
                  );
                  
                  if (departments.length === 0 && supervisorsForEmp.length === 0) return null;
                  
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {/* Departments */}
                      {departments.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                            Team:
                          </span>
                {departments.map((dept, idx) => (
                            <span 
                              key={dept.id || idx}
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: '#fff',
                                background: 'rgba(255,255,255,0.2)',
                                padding: '3px 8px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <FiUsers size={10} />
                              {dept.name}
                            </span>
                          ))}
                {!!primaryDept?.name && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: '#fff',
                      background: 'rgba(255,255,255,0.3)',
                      padding: '3px 8px',
                      borderRadius: 6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: '1px solid rgba(255,255,255,0.35)'
                    }}
                    title="Primary department"
                  >
                    Primary: {primaryDept.name}
                  </span>
                )}
                        </div>
                      )}
                      {/* Supervisors */}
                      {supervisorsForEmp.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                            Reports to:
                          </span>
                          {supervisorsForEmp.map((sup, idx) => (
                            <span 
                              key={sup.id || idx}
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: '#fff',
                                background: 'rgba(255,255,255,0.15)',
                                padding: '3px 8px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <FiUserCheck size={10} />
                              {sup.firstName} {sup.lastName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Stats (4-up) on RHS of header */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginLeft: 'auto' }}>
                <div
                  className="emp-header-stats"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(110px, 1fr))',
                    gap: 10,
                    alignSelf: 'stretch'
                  }}
                >
                  <div style={{ textAlign: 'center', padding: '10px 8px', background: 'rgba(255,255,255,0.12)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>{attendanceRate}%</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.72)', marginTop: 4, fontWeight: 700 }}>Attendance</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px 8px', background: 'rgba(255,255,255,0.12)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>{totalHours.toFixed(0)}h</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.72)', marginTop: 4, fontWeight: 700 }}>Hours</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px 8px', background: 'rgba(255,255,255,0.12)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>{records.length}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.72)', marginTop: 4, fontWeight: 700 }}>Days</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px 8px', background: 'rgba(255,255,255,0.12)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(10px)' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>{onTimeRate}%</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.72)', marginTop: 4, fontWeight: 700 }}>On Time</div>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedEmployee(null); setSelectedDate(null); setSelectedLogKey(null); setExpandedMaps({}); setFocusedMapPoint(null); setShowEnrolledFace(false); }}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    fontWeight: 400,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.15s ease',
                    marginTop: 0
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.20)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {/* Stats moved into header RHS */}

          {/* Location stats */}
          <div style={{
            padding: '12px 20px',
            background: '#fff',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Location compliance {primaryDept?.name ? `• ${primaryDept.name}` : ''}
              </div>
              <div style={{ fontSize: 12, color: colors.text }}>
                {locationStats.expected > 0
                  ? <>GPS captured <b>{locationStats.captured}</b>/<b>{locationStats.expected}</b>{locationStats.captureRate != null ? ` (${locationStats.captureRate}%)` : ''} • Missing <b>{locationStats.missing}</b></>
                  : <>No location requirement for these records</>}
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {locationStats.geofencingEnabled
                  ? <>Geofence: {locationStats.radiusM}m • Inside <b>{locationStats.inside}</b> • Outside <b>{locationStats.outside}</b>{locationStats.avgKm != null ? ` • Avg ${formatDistance(locationStats.avgKm)}` : ''}</>
                  : <>Geofence not configured for primary department (distance stats unavailable)</>}
              </div>
            </div>
            {officeCenter && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, maxWidth: 420 }}>
                {!!effectiveSettings?.geofencingLocation?.address && (
                  <div style={{ fontSize: 12, color: colors.text, fontWeight: 700, textAlign: 'right' }}>
                    {effectiveSettings.geofencingLocation.address}
                  </div>
                )}
                <button
                  onClick={() => setFocusedMapPoint({ lat: officeCenter.lat, lng: officeCenter.lng, kind: 'OFFICE' })}
                  style={{ border: 'none', background: 'transparent', fontSize: 12, fontWeight: 800, color: colors.today, cursor: 'pointer', padding: 0 }}
                >
                  View office pin
                </button>
              </div>
            )}
          </div>

          {/* Schedule settings from department/company config */}
          {effectiveSettings && (
            <div style={{
              padding: '10px 20px',
              background: '#fff',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCalendar size={13} style={{ color: colors.today, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 800, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Schedule</span>
              </div>
              <div style={{ fontSize: 12, color: colors.text }}>
                <b>{(effectiveSettings.workingDays || []).map(d => d.charAt(0).toUpperCase() + d.slice(0, 3).slice(1)).join(', ')}</b>
              </div>
              <div style={{ width: 1, height: 16, background: colors.border, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: colors.text }}>
                <FiClock size={11} style={{ marginRight: 4, verticalAlign: '-1px', color: colors.textMuted }} />
                <b>{formatHHMM12(effectiveSettings.workStartTime || '09:00')}</b> - <b>{formatHHMM12(effectiveSettings.workEndTime || '17:00')}</b>
              </div>
              {effectiveSettings.lunchBreakStart && effectiveSettings.lunchBreakEnd && (
                <>
                  <div style={{ width: 1, height: 16, background: colors.border, flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: colors.textMuted }}>
                    Lunch {formatHHMM12(effectiveSettings.lunchBreakStart)} - {formatHHMM12(effectiveSettings.lunchBreakEnd)}
                  </div>
                </>
              )}
              {effectiveSettings.graceTimeInMinutes > 0 && (
                <>
                  <div style={{ width: 1, height: 16, background: colors.border, flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: colors.textMuted }}>
                    Grace: {effectiveSettings.graceTimeInMinutes}m
                  </div>
                </>
              )}
              <div style={{ marginLeft: 'auto', fontSize: 10, color: colors.textMuted, fontStyle: 'italic' }}>
                {effectiveSource?.type === 'department'
                  ? `from ${effectiveSource.department?.name || 'dept'} settings`
                  : effectiveSource?.type === 'company'
                    ? 'from company settings'
                    : 'default schedule'}
              </div>
            </div>
          )}

          {/* Face Verification Summary - Always show if employee is enrolled or has any verification */}
          {(emp.faceEnrolled || emp.enrolledFaceUrl || records.some(r => r.checkInVerification || r.checkOutVerification)) && (
            <div style={{
              padding: '10px 20px',
              background: colors.todayBg,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'center'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (emp.enrolledFaceUrl) setShowEnrolledFace(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'transparent',
                  border: 'none',
                  cursor: emp.enrolledFaceUrl ? 'pointer' : 'default',
                  padding: 0,
                  opacity: emp.enrolledFaceUrl ? 1 : 0.7
                }}
                title={emp.enrolledFaceUrl ? 'Click to view enrolled face' : 'No enrolled face available'}
              >
                <FiCamera size={16} color={colors.today} />
                <span style={{ fontSize: 11, fontWeight: 800, color: colors.today, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Face Recognition
                </span>
              </button>
              
              {emp.faceReEnrollmentPending ? (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: '#FFF3E0',
                  color: '#E65100',
                  border: '1px solid #FFB74D'
                }}>
                  ⏳ Re-enrollment Pending Approval
                </span>
              ) : emp.faceEnrolled ? (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: colors.presentBg,
                  color: colors.present,
                  border: `1px solid ${colors.present}30`
                }}>
                  Enrolled {emp.faceEnrolledAt ? `on ${new Date(emp.faceEnrolledAt).toLocaleDateString()}` : ''}
                </span>
              ) : (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: colors.lateBg,
                  color: colors.late,
                  border: `1px solid ${colors.late}30`
                }}>
                  Not Enrolled
                </span>
              )}
              
              {/* Approval buttons for pending re-enrollment */}
              {emp.faceReEnrollmentPending && (
                <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`Approve face re-enrollment for ${emp.firstName} ${emp.lastName}?`)) {
                        try {
                          const { data } = await api.post(`/api/face/approve-reenrollment/${emp.id}`);
                          if (data.is_success) {
                            alert('Re-enrollment approved successfully!');
                            // Update modal immediately
                            setSelectedEmployee(prev => prev ? {
                              ...prev,
                              employee: { ...prev.employee, faceReEnrollmentPending: false }
                            } : null);
                            refreshAttendance();
                          } else {
                            alert('Failed to approve: ' + data.message);
                          }
                        } catch (err) {
                          console.error('Approve error:', err);
                          alert('Failed to approve re-enrollment');
                        }
                      }
                    }}
                    style={{
                      padding: '4px 12px',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 6,
                      border: 'none',
                      background: colors.present,
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const reason = window.prompt(`Reject re-enrollment for ${emp.firstName} ${emp.lastName}? Enter reason:`);
                      if (reason !== null) {
                        try {
                          const { data } = await api.post(`/api/face/reject-reenrollment/${emp.id}`, { reason });
                          if (data.is_success) {
                            alert('Re-enrollment rejected. Employee will need to re-enroll.');
                            // Update modal immediately
                            setSelectedEmployee(prev => prev ? {
                              ...prev,
                              employee: { ...prev.employee, faceReEnrollmentPending: false, faceEnrolled: false }
                            } : null);
                            refreshAttendance();
                          } else {
                            alert('Failed to reject: ' + data.message);
                          }
                        } catch (err) {
                          console.error('Reject error:', err);
                          alert('Failed to reject re-enrollment');
                        }
                      }
                    }}
                    style={{
                      padding: '4px 12px',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 6,
                      border: 'none',
                      background: colors.absent,
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    ✗ Reject
                  </button>
                </div>
              )}
              
              {(() => {
                const sims = records
                  .map(r => r.checkInVerification?.similarity)
                  .filter(s => s != null);
                const avgSimilarity = sims.length > 0 ? sims.reduce((a, b) => a + b, 0) / sims.length : null;
                if (avgSimilarity == null) return null;
                return (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: avgSimilarity >= 90 ? colors.presentBg : avgSimilarity >= 70 ? colors.lateBg : colors.absentBg,
                    color: avgSimilarity >= 90 ? colors.present : avgSimilarity >= 70 ? colors.late : colors.absent,
                    border: `1px solid ${colors.border}`
                  }}>
                    Avg Match: {avgSimilarity.toFixed(1)}%
                  </span>
                );
              })()}
              
            </div>
          )}


          {/* Records */}
          <div className="emp-modal-split" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <div ref={modalRecordsRef} style={{ flex: 1, overflow: 'auto', padding: '20px 20px 24px', minWidth: 0 }}>
            {/* Daily hours chart */}
            <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${colors.border}`, borderRadius: 12, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Logged hours per day
                </div>
                <div style={{ fontSize: 11, color: colors.textMuted }}>
                  Max: {maxDailyHours.toFixed(1)}h
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, overflowX: 'auto', paddingBottom: 8, paddingTop: 2 }}>
                {dailyHoursSeries.map((d) => {
                  const h = d.hours || 0;
                  const pct = Math.max(0, Math.min(1, h / maxDailyHours));
                  const barH = 54 * pct + 4;
                  const barColor = h >= 8 ? colors.present : h >= 6 ? colors.today : h >= 4 ? colors.late : h > 0 ? colors.partial : colors.border;
                  return (
                    <button
                      key={d.dateStr}
                      onClick={() => {
                        setSelectedLogKey(null);
                        setSelectedDate(d.dateStr);
                      }}
                      style={{
                        width: 14,
                        flexShrink: 0,
                        textAlign: 'center',
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer'
                      }}
                      title={`Scroll to ${d.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}`}
                    >
                      <div
                        title={`${d.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}: ${h.toFixed(1)}h`}
                        style={{
                          height: barH,
                          borderRadius: 6,
                          background: barColor,
                          opacity: d.isWeekend ? 0.5 : 1
                        }}
                      />
                      <div style={{ fontSize: 8, color: colors.textMuted, marginTop: 4 }}>
                        {d.day}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: colors.presentBg, color: colors.present, border: `1px solid ${colors.border}` }}>8h+</div>
                <div style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: colors.todayBg, color: colors.today, border: `1px solid ${colors.border}` }}>6-8h</div>
                <div style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: colors.lateBg, color: colors.late, border: `1px solid ${colors.border}` }}>4-6h</div>
                <div style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: colors.partialBg, color: colors.partial, border: `1px solid ${colors.border}` }}>0-4h</div>
              </div>
            </div>

            <div style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: colors.textMuted, 
              marginBottom: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Attendance Records</span>
              <span style={{ fontSize: 11, fontWeight: 400, textTransform: 'none' }}>{records.length} entries</span>
            </div>

            {/* Expand records to show each day for multi-day sessions - reversed for descending timeline */}
              {records.flatMap((record, idx) => {
              const daysDiff = getDaysDifference(record.checkInTime, record.checkOutTime);
              const checkInDate = new Date(record.checkInTime);
              const entries = [];
              const groupKey = getLogGroupKey(empId, record) || `${idx}`;
              
              // Build entries in reverse order (most recent first for descending timeline)
              if (daysDiff > 0) {
                // First add checkout day (most recent)
                const checkOutDate = new Date(checkInDate);
                checkOutDate.setDate(checkOutDate.getDate() + daysDiff);
                const continuationDates = [];
                for (let i = daysDiff - 1; i >= 1; i--) {
                  const intermediateDate = new Date(checkInDate);
                  intermediateDate.setDate(intermediateDate.getDate() + i);
                  continuationDates.push(intermediateDate);
                }
                entries.push({
                  ...record,
                  displayDate: checkOutDate,
                  entryType: 'checkout',
                  dayIndex: daysDiff,
                  totalDays: daysDiff + 1,
                  groupKey,
                  continuationDates,
                  key: `${groupKey}-checkout`
                });
              }
              
              // Finally add check-in day (oldest - at bottom)
              entries.push({
                ...record,
                displayDate: checkInDate,
                entryType: 'checkin',
                dayIndex: 0,
                totalDays: daysDiff + 1,
                groupKey,
                key: `${groupKey}-checkin`
              });
              
              return entries;
            }).map((entry) => {
              const workedHours = parseFloat(entry.totalHours) || 0;
              const breakCount = Array.isArray(entry.breaks) ? entry.breaks.length : 0;
              const recordKey = entry.key;
              const isBreaksExpanded = !!expandedBreaks[recordKey];
              const recordDate = entry.displayDate.toDateString();
              const isHighlighted = selectedLogKey
                ? selectedLogKey === entry.groupKey
                : selectedDate === recordDate;
              
              // Determine display info based on entry type
              const isCheckIn = entry.entryType === 'checkin';
              const isCheckOut = entry.entryType === 'checkout';
              const isContinuation = entry.entryType === 'continuation';

              const loc = isCheckIn ? entry.checkInLocation : isCheckOut ? entry.checkOutLocation : null;
              const locLatLng = getLatLngFromLocation(loc);
              const officeLatLng = officeCenter;
              const distanceKm = officeLatLng && locLatLng ? haversineKm(officeLatLng, locLatLng) : null;
              const radiusM = locationStats?.radiusM;
              const withinFence = (distanceKm != null && radiusM != null) ? (distanceKm * 1000 <= radiusM) : null;
              const checkInLate = isCheckIn ? isLateCheckInTime(entry.checkInTime, effectiveSettings) : null;
              const lateByMins = isCheckIn ? getLateByMinutes(entry.checkInTime, effectiveSettings) : null;
              
              let statusLabel, statusBg, statusColor;
              if (isCheckIn) {
                statusLabel = 'IN';
                statusBg = colors.presentBg;
                statusColor = colors.present;
              } else if (isCheckOut) {
                statusLabel = 'OUT';
                statusBg = colors.todayBg;
                statusColor = colors.today;
              } else {
                statusLabel = '...';
                statusBg = colors.partialBg;
                statusColor = colors.partial;
              }

              return (
                <div
                  key={recordKey}
                  data-date={recordDate}
                  style={{
                    background: isHighlighted ? colors.todayBg : '#fff',
                    border: isHighlighted ? `2px solid ${colors.today}` : `1px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 6,
                    transition: 'all 0.2s ease',
                    boxShadow: isHighlighted ? `0 2px 8px ${colors.today}20` : 'none',
                    // Visual connector for multi-day sessions
                    ...(entry.totalDays > 1 && {
                      borderLeft: `3px solid ${isCheckIn ? colors.present : isCheckOut ? colors.today : colors.partial}`,
                      marginLeft: isContinuation ? 10 : 0,
                      marginBottom: isCheckIn ? 12 : 4
                    })
                  }}
                  onClick={() => {
                    setSelectedLogKey(entry.groupKey || null);
                    setSelectedDate(recordDate);
                    if ((isCheckIn || isCheckOut) && locLatLng) {
                      setFocusedMapPoint({
                        lat: locLatLng.lat,
                        lng: locLatLng.lng,
                        kind: isCheckIn ? 'IN' : 'OUT',
                        time: isCheckIn ? entry.checkInTime : entry.checkOutTime,
                        address: loc?.address || null,
                        groupKey: entry.groupKey || null
                      });
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: statusBg,
                      color: statusColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {statusLabel}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: colors.text }}>
                        {entry.displayDate.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                       {/*  {entry.totalDays > 1 && (
                          <span style={{ fontSize: 9, color: colors.textMuted, marginLeft: 6 }}>
                            ({entry.dayIndex + 1}/{entry.totalDays})
                          </span>
                        )} */}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                        {isCheckIn && (
                          <>
                            <span style={{ color: checkInLate ? colors.absent : colors.textMuted, fontWeight: checkInLate ? 800 : 500 }}>
                              {formatTime(entry.checkInTime)}
                            </span>
                            {entry.totalDays === 1 && entry.checkOutTime && (
                              <> → {formatTime(entry.checkOutTime)}</>
                            )}
                          </>
                        )}
                        {isContinuation && (
                          <span style={{ fontStyle: 'italic', opacity: 0.6 }}>ongoing</span>
                        )}
                        {isCheckOut && formatTime(entry.checkOutTime)}
                      </div>
                    </div>
                    {/* Right side badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {/* Face verification badge */}
                      {(() => {
                        const verification = isCheckIn ? entry.checkInVerification : (isCheckOut ? entry.checkOutVerification : null);
                        if (!verification) return null;
                        
                        return (
                          <div
                            title={`${verification.success ? 'Verified' : 'Failed'}${verification.similarity != null ? ` • ${verification.similarity.toFixed(1)}% match` : ''}${verification.livenessConfidence != null ? ` • Liveness: ${verification.livenessConfidence.toFixed(0)}%` : ''}${!verification.success && verification.failureReason ? ` • ${verification.failureReason}` : ''}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 9,
                              fontWeight: 800,
                              padding: '3px 6px',
                              borderRadius: 999,
                              border: `1px solid ${colors.border}`,
                              background: verification.success ? colors.presentBg : colors.absentBg,
                              color: verification.success ? colors.present : colors.absent
                            }}
                          >
                            <FiShield size={10} />
                            {verification.similarity != null ? `${verification.similarity.toFixed(0)}%` : (verification.success ? '✓' : '✗')}
                          </div>
                        );
                      })()}
                      
                      {isCheckIn && checkInLate != null && (
                        <div style={{
                          fontSize: 10,
                          fontWeight: 900,
                          padding: '3px 8px',
                          borderRadius: 999,
                          border: `1px solid ${colors.border}`,
                          background: checkInLate ? colors.lateBg : colors.presentBg,
                          color: checkInLate ? colors.late : colors.present
                        }}>
                          {checkInLate
                            ? `Late by ${formatLateBy(lateByMins) || ''}`.trim()
                            : 'On time'}
                        </div>
                      )}
                      {isCheckIn && (
                        <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>
                          {workedHours.toFixed(1)}h
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ongoing days (rendered as a row of chips instead of separate stacked cards) */}
                  {isCheckOut && Array.isArray(entry.continuationDates) && entry.continuationDates.length > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${colors.border}` }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                        Ongoing days
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {entry.continuationDates.map((d, i) => {
                          const dateStr = d.toDateString();
                          const isActive = selectedDate === dateStr || selectedLogKey === entry.groupKey;
                          return (
                            <button
                              key={`${entry.groupKey}-contchip-${i}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLogKey(entry.groupKey || null);
                                setSelectedDate(dateStr);
                              }}
                              style={{
                                border: `1px solid ${isActive ? colors.today : colors.border}`,
                                background: isActive ? colors.todayBg : colors.weekend,
                                color: isActive ? colors.today : colors.textMuted,
                                padding: '6px 10px',
                                borderRadius: 999,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 800
                              }}
                              title={`Ongoing: ${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}`}
                            >
                              {d.toLocaleDateString([], { weekday: 'short', day: 'numeric' })}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Location (if available) */}
                  {(isCheckIn || isCheckOut) && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${colors.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.text, marginTop: 0, wordBreak: 'break-word' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                              {isCheckIn ? 'Check-in location' : 'Check-out location'}:
                            </span>{' '}
                            <span>
                              {loc?.address || (locLatLng ? `${locLatLng.lat.toFixed(5)}, ${locLatLng.lng.toFixed(5)}` : 'N/A')}
                            </span>
                          </div>
                          {distanceKm != null && (
                            <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span>{formatDistance(distanceKm)} from office{radiusM != null ? ` • radius ${radiusM}m` : ''}</span>
                              {withinFence != null && (
                                <span style={{
                                  fontSize: 10,
                                  fontWeight: 900,
                                  padding: '2px 6px',
                                  borderRadius: 999,
                                  border: `1px solid ${colors.border}`,
                                  background: withinFence ? colors.presentBg : colors.absentBg,
                                  color: withinFence ? colors.present : colors.absent
                                }}>
                                  {withinFence ? 'Inside' : 'Outside'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Clicking the card zooms the RHS map; no per-card map button */}
                      </div>
                    </div>
                  )}


                  {/* Breaks - only show on check-in day */}
                  {isCheckIn && breakCount > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedBreaks(prev => ({ ...prev, [recordKey]: !prev[recordKey] }));
                        }}
                        style={{
                          background: colors.weekend,
                          border: 'none',
                          borderRadius: 6,
                          padding: '5px 12px',
                          color: colors.textMuted,
                          fontSize: 11,
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        {breakCount} break{breakCount > 1 ? 's' : ''} {isBreaksExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                  )}

                  {isCheckIn && isBreaksExpanded && entry.breaks?.map((brk, bIdx) => (
                    <div key={bIdx} style={{
                      marginTop: 8,
                      padding: 10,
                      background: colors.weekend,
                      borderRadius: 8,
                      fontSize: 11
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: colors.text, fontWeight: 500 }}>{brk.reason || 'Break'}</span>
                        {!brk.endTime && <span style={{ background: colors.lateBg, color: colors.late, padding: '2px 6px', borderRadius: 4, fontSize: 10 }}>Ongoing</span>}
                      </div>
                      <div style={{ color: colors.textMuted }}>
                        {formatTime(brk.startTime)}{brk.endTime && ` - ${formatTime(brk.endTime)}`}
                        {brk.duration && ` • ${parseFloat(brk.duration).toFixed(1)}h`}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {records.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
                No attendance records for this month
              </div>
            )}
          </div>
            <div
              className="emp-modal-map"
              style={{
                width: 680,
                borderLeft: `1px solid ${colors.border}`,
                background: '#fff',
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10
              }}
            >
             
              <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${colors.border}`, background: colors.weekend, flex: 1, minHeight: 420, position: 'relative' }}>
                <button
                  onClick={() => {
                    setFocusedMapPoint(null);
                    setMapResetTick(t => t + 1);
                  }}
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1000,
                    border: `1px solid ${colors.border}`,
                    background: '#fff',
                    color: colors.textMuted,
                    padding: '8px 10px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 900,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                  }}
                  title="Reset map view"
                >
                  Reset view
                </button>
                <MapContainer
                  center={consolidatedPoints.length ? [consolidatedPoints[0].lat, consolidatedPoints[0].lng] : [20.5937, 78.9629]}
                  zoom={consolidatedPoints.length ? 12 : 4}
                  scrollWheelZoom={true}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {!!consolidatedBounds && (
                    <FitBounds
                      bounds={consolidatedBounds}
                      boundsKey={consolidatedBoundsKey}
                      enabled={!focusedMapPoint}
                    />
                  )}
                  {!!focusedMapPoint && <FlyToPoint point={focusedMapPoint} />}
                  <ClusteredPointsLayer
                    points={consolidatedPoints}
                    officeCenter={officeCenter}
                    icons={{ office: iconOffice, inOnTime: iconInOnTime, inLate: iconInLate, out: iconOut }}
                    onPointClick={(p) => {
                      // Clicking markers should scroll the list (not re-zoom)
                      try {
                        if (p?.groupKey) setSelectedLogKey(p.groupKey);
                        if (p?.time) setSelectedDate(new Date(p.time).toDateString());
                      } catch {}
                    }}
                  />
                </MapContainer>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, fontWeight: 900, padding: '4px 10px', borderRadius: 999, background: colors.presentBg, color: colors.present, border: `1px solid ${colors.border}` }}>IN</div>
                <div style={{ fontSize: 11, fontWeight: 900, padding: '4px 10px', borderRadius: 999, background: colors.lateBg, color: colors.late, border: `1px solid ${colors.border}` }}>LATE</div>
                <div style={{ fontSize: 11, fontWeight: 900, padding: '4px 10px', borderRadius: 999, background: colors.partialBg, color: colors.partial, border: `1px solid ${colors.border}` }}>OUT</div>
                {officeCenter && <div style={{ fontSize: 11, fontWeight: 900, padding: '4px 10px', borderRadius: 999, background: colors.todayBg, color: colors.today, border: `1px solid ${colors.border}` }}>OFFICE</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Early returns
  if (!isAuthenticated) {
    return (
      <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
        <div className="page-content">
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text }}>Attendance</h2>
          <div style={{ color: colors.textMuted, marginTop: 16 }}>Please log in to view attendance data</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
        <div className="page-content">
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text }}>Field Staff Attendance</h2>
          <div style={{
            marginTop: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textMuted,
            fontSize: 14,
            gap: 16
          }}>
            <div style={{
              width: 44,
              height: 44,
              border: `3px solid ${colors.border}`,
              borderTop: `3px solid ${colors.today}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Loading attendance data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
      <style>{`
        .attendance-cell {
          width: 100%;
          min-height: 44px;
          border-radius: 6px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
          font-size: 9px;
          padding: 3px 2px;
          gap: 1px;
        }
        .attendance-cell:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          z-index: 5;
        }
        .attendance-cell .time-in {
          font-weight: 600;
          font-size: 9px;
          line-height: 1.05;
          white-space: nowrap;
        }
        .attendance-cell .time-out {
          font-size: 9px;
          font-weight: 500;
          line-height: 1.05;
          white-space: nowrap;
        }
        .employee-row:hover {
          background: #f1f5f9;
        }
        .month-nav-btn {
          padding: 8px 12px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.15s ease;
        }
        .month-nav-btn:hover {
          background: rgba(0,0,0,0.05);
          color: #1e293b;
        }
        .stat-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        /* Remove default Leaflet div-icon box/border behind our custom "pill" markers */
        .leaflet-div-icon.wl-att-pin,
        .leaflet-div-icon.wl-att-cluster,
        .leaflet-marker-icon.wl-att-pin,
        .leaflet-marker-icon.wl-att-cluster {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        /* Shrink any remaining cluster halo/padding */
        .wl-att-cluster {
          padding: 0 !important;
        }
        /* Employee modal: stack map under records on smaller screens */
        @media (max-width: 980px) {
          .emp-modal-split {
            flex-direction: column !important;
          }
          .emp-modal-map {
            width: 100% !important;
            border-left: none !important;
            border-top: 1px solid #e2e8f0 !important;
          }
        }
        @media (max-width: 1100px) {
          .emp-header-row {
            flex-wrap: wrap !important;
          }
          .emp-header-stats {
            width: 100% !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          }
        }
      `}</style>

      <div className="page-content">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text, fontWeight: 700 }}>Field Staff Attendance</h2>
          
          {/* Month Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="month-nav-btn" onClick={goToPrevMonth}>
              <FiChevronLeft size={18} />
            </button>
            <div style={{
              padding: '8px 16px',
              background: 'transparent',
              fontWeight: 600,
              color: colors.text,
              fontSize: 16,
              minWidth: 150,
              textAlign: 'center'
            }}>
              {months[selectedMonth]} {selectedYear}
            </div>
            <button className="month-nav-btn" onClick={goToNextMonth}>
              <FiChevronRight size={18} />
            </button>
            <button
              className="month-nav-btn"
              onClick={goToToday}
              style={{ marginLeft: 8, padding: '8px 16px', fontWeight: 500, fontSize: 13 }}
            >
              Today
            </button>
            <button
              className="month-nav-btn"
              onClick={() => setShowSettings(true)}
              style={{ marginLeft: 8, padding: '8px 12px' }}
              title="Attendance settings"
            >
              <FiSettings size={18} />
            </button>

            {/* Export */}
            <div ref={exportMenuRef} style={{ position: 'relative' }}>
              <button
                className="month-nav-btn"
                onClick={() => setShowExportMenu(v => !v)}
                style={{ padding: '8px 12px' }}
                title="Export"
              >
                <FiDownload size={18} />
              </button>
              {showExportMenu && (
                <div style={{
                  position: 'absolute',
                  top: 42,
                  right: 0,
                  background: '#fff',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 12,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  minWidth: 220,
                  zIndex: 50
                }}>
                  <button
                    onClick={() => { setShowExportMenu(false); exportCSV(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: colors.text }}
                  >
                    Export CSV (Master)
                  </button>
                  <button
                    onClick={() => { setShowExportMenu(false); exportXLSX(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: colors.text, borderTop: `1px solid ${colors.border}` }}
                  >
                    Export Excel (.xlsx)
                    <div style={{ fontSize: 11, fontWeight: 500, color: colors.textMuted, marginTop: 2 }}>
                      Master + per employee sheets
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: colors.absentBg,
            border: `1px solid ${colors.absent}40`,
            borderRadius: 10,
            padding: '12px 16px',
            color: colors.absent,
            fontSize: 13,
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: colors.absent,
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                color: '#fff',
                fontSize: 12,
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiUsers size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.totalEmployees}</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>Total Staff</div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiCheck size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.avgAttendance}%</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>Avg Attendance</div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiActivity size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.onTimeRate}%</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>On Time Rate</div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiClock size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.totalHours}h</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>Total Hours</div>
            </div>
          </div>

          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiClock size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.overtimeHours}h</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>Overtime (est.)</div>
            </div>
          </div>

          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiArrowRight size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>
              {stats.remoteRate != null ? ` ${stats.remoteRate}%` : ''}
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>
                Remote
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
              <FiAlertCircle size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{stats.gpsCaptureRate != null ? `${stats.gpsCaptureRate}%` : ''}</div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 300 }}>
              {stats.gpsMissing} Missing GPS
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          gap: 20,
          marginBottom: 16,
          padding: '12px 16px',
          background: '#fff',
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: colors.presentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.present }}><FiCheck size={11} /></div>
            Present
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: colors.lateBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.late }}><FiAlertCircle size={11} /></div>
            Late
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: colors.partialBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.partial }}><FiMinus size={11} /></div>
            Partial
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: colors.absentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.absent }}><FiX size={11} /></div>
            Absent
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: colors.weekend, border: `1px solid ${colors.border}` }}></div>
            Weekend
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, background: '#fff', border: `2px solid ${colors.today}` }}></div>
            Today
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 36, height: 28, borderRadius: 5, background: colors.presentBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: colors.text, fontSize: 7, fontWeight: 600, lineHeight: 1.2, border: `1px solid ${colors.present}30` }}>
              <span style={{ color: colors.present }}>9:00</span>
              <span>+1d</span>
              <span>2:30</span>
            </div>
            Multi-day
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 28, height: 28, borderRadius: 5, background: colors.todayBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: colors.text, fontSize: 8, fontWeight: 600, lineHeight: 1.2, border: `1px solid ${colors.today}30` }}>
              <span>⟷</span>
              <span style={{ fontSize: 7 }}>cont.</span>
            </div>
            Ongoing
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textMuted }}>
            <div style={{ width: 28, height: 28, borderRadius: 5, background: colors.todayBg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: colors.text, fontSize: 7, fontWeight: 600, lineHeight: 1.2, border: `1px solid ${colors.today}30` }}>
              <span>OUT</span>
              <span style={{ fontSize: 8 }}>2:30</span>
            </div>
            Checkout
          </div>
        </div>

        {/* Main Attendance Grid */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          {mergedEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
              <FiCalendar size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div style={{ fontSize: 15, marginBottom: 8, fontWeight: 500 }}>No employees found</div>
              <div style={{ fontSize: 13 }}>No field staff have been added to the company yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{
                      position: 'sticky',
                      left: 0,
                      background: '#f8fafc',
                      padding: '14px 16px',
                      textAlign: 'left',
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textMuted,
                      borderBottom: `1px solid ${colors.border}`,
                      minWidth: 200,
                      zIndex: 10
                    }}>
                      Employee
                    </th>
                    {daysInMonth.map(day => (
                      <th
                        key={day.day}
                        style={{
                          padding: '10px 4px',
                          textAlign: 'center',
                          fontSize: 10,
                          fontWeight: 600,
                          color: day.isToday ? colors.today : day.isWeekend ? colors.textMuted : colors.text,
                          borderBottom: `1px solid ${colors.border}`,
                          background: day.isToday ? colors.todayBg : day.isWeekend ? colors.weekend : '#f8fafc',
                          minWidth: 70,
                          width: 70
                        }}
                      >
                        <div style={{ marginBottom: 2, fontSize: 9 }}>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.dayOfWeek]}</div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{day.day}</div>
                      </th>
                    ))}
                    <th style={{
                      padding: '14px 16px',
                      textAlign: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textMuted,
                      borderBottom: `1px solid ${colors.border}`,
                      minWidth: 80,
                      background: '#f8fafc'
                    }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mergedEmployees.map((emp, empIndex) => {
                    const empData = emp.employee || {};
                    const empId = empData.id;
                    const initials = `${empData.firstName?.charAt(0) || ''}${empData.lastName?.charAt(0) || ''}`.toUpperCase() || '?';
                    const empSettings = getEffectiveAttendanceSettingsForEmployee(empData)?.settings;
                    const presentDays = (emp.records || []).length;
                    const workingDays = daysInMonth.filter(d => !isNonWorkingDay(d.dayOfWeek, empSettings)).length;
                    const attendanceRate = workingDays > 0 ? ((presentDays / workingDays) * 100).toFixed(0) : 0;

                    return (
                      <tr key={empId || empIndex} className="employee-row" style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td
                          style={{
                            position: 'sticky',
                            left: 0,
                            background: '#fff',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            zIndex: 5
                          }}
                          onClick={() => setSelectedEmployee({ ...emp, index: empIndex })}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {empData.profilePicture ? (
                              <img
                                src={empData.profilePicture}
                                alt={`${empData.firstName} ${empData.lastName}`}
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                  flexShrink: 0
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: getAvatarColor(empIndex),
                              display: empData.profilePicture ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: 12,
                              flexShrink: 0
                            }}>
                              {initials}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {empData.firstName || 'Unknown'} {empData.lastName || ''}
                                {empData.faceReEnrollmentPending && (
                                  <span 
                                    title="Face re-enrollment pending approval"
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      padding: '2px 6px',
                                      borderRadius: 4,
                                      background: '#FFF3E0',
                                      color: '#E65100',
                                      border: '1px solid #FFB74D',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    ⏳ PENDING
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: colors.textMuted, whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {empData.email || empData.role || 'Employee'}
                              </div>
                            </div>
                          </div>
                        </td>
                        {daysInMonth.map(day => {
                          const cell = getCellContent(empId, day, empSettings);
                          let bgColor, iconColor, barColor;

                          switch (cell.type) {
                            case 'present':
                              bgColor = colors.presentBg;
                              iconColor = colors.present;
                              barColor = colors.present;
                              break;
                            case 'late':
                              bgColor = colors.lateBg;
                              iconColor = colors.late;
                              barColor = colors.late;
                              break;
                            case 'partial':
                              bgColor = colors.partialBg;
                              iconColor = colors.partial;
                              barColor = colors.partial;
                              break;
                            case 'absent':
                              bgColor = colors.absentBg;
                              iconColor = colors.absent;
                              barColor = colors.absent;
                              break;
                            case 'continuation':
                              bgColor = colors.todayBg;
                              iconColor = colors.today;
                              barColor = colors.today;
                              break;
                            case 'checkout':
                              bgColor = colors.todayBg;
                              iconColor = colors.today;
                              barColor = colors.today;
                              break;
                            case 'weekend':
                            default:
                              bgColor = colors.weekend;
                              iconColor = colors.textMuted;
                              barColor = colors.textMuted;
                          }

                          return (
                            <td key={day.day} style={{ padding: '4px 2px', textAlign: 'center', position: 'relative' }}>
                              <div
                                className="attendance-cell"
                                style={{
                                  background: bgColor,
                                  color: iconColor,
                                  margin: '0 auto',
                                  border: day.isToday ? `2px solid ${colors.today}` : `1px solid ${iconColor}30`,
                                  position: 'relative',
                                  overflow: 'visible'
                                }}
                                title={(() => {
                                  if (!cell.record) return day.isWeekend ? 'Weekend' : 'Absent';
                                  const base = `${formatTimeRange(cell.record)} • ${cell.record.totalHours || 0}h worked${getDaysDifference(cell.record.checkInTime, cell.record.checkOutTime) > 0 ? ` (checkout: ${formatShortDate(cell.record.checkOutTime)})` : ''}`;
                                  const empSettings = getEffectiveAttendanceSettingsForEmployee(empData)?.settings;
                                  const requireLoc = empSettings?.requireLocationForAttendance !== undefined ? !!empSettings.requireLocationForAttendance : true;
                                  if (!requireLoc) return base;
                                  const hasIn = !!getLatLngFromLocation(cell.record.checkInLocation);
                                  const hasOut = cell.record.checkOutTime ? !!getLatLngFromLocation(cell.record.checkOutLocation) : true;
                                  const locLine = ` • GPS: IN ${hasIn ? '✓' : '✗'} / OUT ${hasOut ? '✓' : '✗'}`;
                                  return base + locLine;
                                })()}
                                onClick={() => {
                                  if (cell.record) {
                                    setSelectedLogKey(getLogGroupKey(empId, cell.record));
                                    setSelectedDate(day.dateStr);
                                    setSelectedEmployee({ ...emp, index: empIndex });
                                  }
                                }}
                              >
                                {/* Weekend - empty */}
                                {cell.type === 'weekend' && null}
                                
                                {/* Absent - just icon */}
                                {cell.type === 'absent' && (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {cell.icon}
                                  </div>
                                )}
                                
                                {/* Continuation day - arrow showing session continues */}
                                {cell.type === 'continuation' && (
                                  <>
                                    <div style={{ fontSize: 10, color: colors.text, fontWeight: 600 }}>⟷</div>
                                    <div style={{ fontSize: 8, color: colors.text, fontWeight: 500 }}>cont.</div>
                                  </>
                                )}
                                
                                {/* Checkout day - show checkout time */}
                                {cell.type === 'checkout' && (
                                  <>
                                    <div style={{ fontSize: 8, color: colors.text, fontWeight: 600 }}>OUT</div>
                                    <div className="time-out" style={{ color: colors.text, fontWeight: 600, fontSize: 9 }}>{cell.checkOutTime}</div>
                                  </>
                                )}
                                
                                {/* Present/Late/Partial - show times */}
                                {(cell.type === 'present' || cell.type === 'late' || cell.type === 'partial') && (
                                  <>
                                    {/* Check-in time */}
                                    {cell.checkInTime && (
                                      <div className="time-in" style={{ color: colors.text }}>
                                        {cell.checkInTime}
                                      </div>
                                    )}
                                    
                                    {/* Check-out time (only if same day) */}
                                    {cell.checkOutTime && !cell.continues && (
                                      <div className="time-out" style={{ color: colors.text }}>
                                        {cell.checkOutTime}
                                      </div>
                                    )}
                                    
                                    {/* Continuation indicator if checkout is on different day */}
                                    {cell.continues && (
                                      <>
                                        <div style={{ fontSize: 8, color: colors.text, fontWeight: 600 }}>+{cell.daysDiff}d</div>
                                        <div style={{ fontSize: 9, color: colors.text, fontWeight: 500 }}>{formatTime(cell.record?.checkOutTime)}</div>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: parseInt(attendanceRate) >= 80 ? colors.present : parseInt(attendanceRate) >= 50 ? colors.late : colors.absent
                          }}>
                            {attendanceRate}%
                          </div>
                          <div style={{ fontSize: 10, color: colors.textMuted }}>{presentDays}/{workingDays} days</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Employee Modal */}
      {renderEmployeeModal()}

      {/* Settings Modal */}
      <AttendanceSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        colors={colors}
        departments={departments}
        selectedDepartmentId={settingsDepartmentId}
        onChangeDepartment={(nextDeptId) => setSettingsDepartmentId(nextDeptId || 'company')}
        settingsDraft={settingsDraft}
        setSettingsDraft={setSettingsDraft}
        isLoading={settingsLoading}
        isSaving={settingsSaving}
        error={settingsApiError}
        onSave={() => saveAttendanceSettings(settingsDepartmentId)}
        onReset={() => loadAttendanceSettings(settingsDepartmentId)}
        isDirty={settingsIsDirty}
        isPersisted={settingsIsPersisted}
        summariesLoading={settingsSummariesLoading}
        summaries={settingsSummaries}
      />

      {/* Enrolled Face Modal - Rendered at root level for proper z-index */}
      {showEnrolledFace && selectedEmployee?.employee?.enrolledFaceUrl && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={() => setShowEnrolledFace(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              maxWidth: 420,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
              cursor: 'default'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: colors.text }}>
              Enrolled Face
            </div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
              {selectedEmployee.employee.firstName} {selectedEmployee.employee.lastName}
            </div>
            <img
              src={selectedEmployee.employee.enrolledFaceUrl}
              alt={`${selectedEmployee.employee.firstName} ${selectedEmployee.employee.lastName} enrolled face`}
              style={{
                width: '100%',
                maxWidth: 320,
                borderRadius: 12,
                border: `2px solid ${colors.border}`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
              }}
            />
            {selectedEmployee.employee.faceEnrolledAt && (
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 16 }}>
                Enrolled on {new Date(selectedEmployee.employee.faceEnrolledAt).toLocaleDateString()} at {new Date(selectedEmployee.employee.faceEnrolledAt).toLocaleTimeString()}
              </div>
            )}
            <button
              onClick={() => setShowEnrolledFace(false)}
              style={{
                marginTop: 20,
                padding: '10px 32px',
                background: colors.today,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
